import {
  useModals,
  Modal,
  ModalProps,
  Button,
  Select,
  SelectItem,
  Stack,
} from "@skailer-com/uikit";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useEmployeeSearchInline, {
  useEmployeesByIds,
} from "@/queries/employees/use-employees.query.ts";
import {
  useEmployeeCustomTableNewRowPrefill,
  useEmployeeCustomTableRowSave,
} from "@/queries/employees/use-employee-custom-table.query.ts";
import {
  ApiCustomFieldValue,
  ApiField,
  ApiFieldTypes,
  ApiTable,
} from "@/types/custom-field.types.ts";
import {
  ApiEmployeeCustomTableEditPayload,
  ApiEmployeeCustomTableRow,
} from "@/types/employees/employee.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { format, isValid } from "date-fns";
import { unionBy } from "lodash";
import { useEffect, useMemo } from "react";
import { Controller, Resolver, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { isCompensationRollupField } from "@/utils/compensation-custom-field.utils.ts";

interface EmployeeCustomTableRecordModalProps extends ModalProps {
  employeeId?: number;
  table: ApiTable;
  row?: ApiEmployeeCustomTableRow;
}

export function EmployeeCustomTableRecordModal({
  employeeId,
  table,
  row,
  ...props
}: EmployeeCustomTableRecordModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = { ...props };
  const { renderEmployeeName } = useFormatter();
  const {
    getSchemaForField,
    getValueForFieldFromValueObject,
    transformFormDataToValue,
    getFieldByType,
  } = useCustomFieldsHook();

  const presetEmployeeId = employeeId && employeeId > 0 ? employeeId : undefined;
  const isEmployeeLocked = !!row || presetEmployeeId !== undefined;
  const editableFields = useMemo(
    () =>
      table.table_fields.filter(
        (field) =>
          field.type !== ApiFieldTypes.changed_by &&
          !field.is_calculated &&
          !isCompensationRollupField(field.type)
      ),
    [table.table_fields]
  );

  const employeeSearch = useEmployeeSearchInline(!isEmployeeLocked);
  const { data: lockedEmployees } = useEmployeesByIds(
    presetEmployeeId ? [presetEmployeeId] : [],
    !!presetEmployeeId
  );

  const employeeOptions = useMemo<SelectItem[]>(() => {
    const loaded: SelectItem[] = (lockedEmployees ?? []).map((e) => ({
      id: e.id,
      title: renderEmployeeName(e),
    }));
    return unionBy<SelectItem>(loaded, employeeSearch.options, "id");
  }, [lockedEmployees, employeeSearch.options, renderEmployeeName]);

  const formConfig = useMemo(() => {
    let schemaFields: yup.ObjectShape = {
      employee_id: yup.number().required().nullable(),
    };
    let values: Record<string, unknown> = {
      employee_id: presetEmployeeId ?? null,
    };

    editableFields.forEach((field) => {
      const cell = row?.cells.find((cell) => cell.field.id === field.id);
      const value =
        field.type === ApiFieldTypes.effective_from
          ? { date_value: row?.date_effective_from ?? new Date() }
          : cell?.value;

      schemaFields = { ...schemaFields, ...getSchemaForField(field) };
      values = { ...values, ...getValueForFieldFromValueObject(field, value, true) };
    });

    return {
      schema: yup.object(schemaFields),
      values,
    };
  }, [editableFields, row, presetEmployeeId]);

  type RecordSchema = { employee_id: number | null } & Record<string, unknown>;

  const formHook = useForm<RecordSchema>({
    mode: "onSubmit",
    resolver: yupResolver(formConfig.schema) as unknown as Resolver<RecordSchema>,
    defaultValues: formConfig.values,
  });

  const {
    control,
    handleSubmit,
    getFieldState,
    setValue,
    formState: { errors },
  } = formHook;

  const linkedFields = useMemo(
    () => editableFields.filter((field) => field.linked_field),
    [editableFields]
  );
  const effectiveFromSlug = editableFields.find(
    (field) => field.type === ApiFieldTypes.effective_from
  )?.slug;
  const formEmployeeId = useWatch({ control, name: "employee_id" });
  const formEffectiveFrom = useWatch({ control, name: effectiveFromSlug ?? "" });
  const prefillDate =
    table.is_historical && formEffectiveFrom instanceof Date && isValid(formEffectiveFrom)
      ? format(formEffectiveFrom, "yyyy-MM-dd")
      : null;
  const { data: prefill } = useEmployeeCustomTableNewRowPrefill(
    formEmployeeId,
    table.id,
    prefillDate,
    !row && linkedFields.length > 0
  );

  useEffect(() => {
    if (!prefill) return;
    linkedFields.forEach((field) => {
      if (getFieldState(field.slug).isDirty) return;
      const cell = prefill.cells.find((cell) => cell.field.id === field.id);
      Object.entries(getValueForFieldFromValueObject(field, cell?.value)).forEach(([name, value]) =>
        setValue(name, value)
      );
    });
  }, [prefill, linkedFields, getFieldState, setValue, getValueForFieldFromValueObject]);

  const rowSave = useEmployeeCustomTableRowSave(control);

  const onSubmit = handleSubmit(async (formData) => {
    const cells: Record<string, ApiCustomFieldValue> = {};

    editableFields.forEach((field) => {
      cells[field.slug] = transformFormDataToValue(field, formData);
    });

    const payload: ApiEmployeeCustomTableEditPayload = {
      cells,
    };

    try {
      await rowSave.mutateAsync({
        employeeId: formData.employee_id as number,
        tableId: table.id,
        rowId: row?.id,
        payload,
        linkedFields: editableFields.flatMap((field) =>
          field.linked_field ? [field.linked_field] : []
        ),
      });

      close(id);
    } catch {
      return;
    }
  });

  const renderFieldByType = (field: ApiField) => {
    return getFieldByType({
      field,
      formHook,
      label: field.title,
      canView: true,
      canEdit: true,
      fieldValue: undefined,
      options: field.options?.map((opt) => ({ id: Number(opt.id), title: opt.title })) ?? [],
    });
  };

  return (
    <Modal
      {...props}
      title={row ? t("common:edit_record") : t("common:add_record")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} isLoading={rowSave.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"sm"}>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Select
                required
                label={t("core:employee")}
                emptyTitle={t("common:select")}
                value={value}
                options={employeeOptions}
                searchable
                showAvatar
                showCaption
                isClearable={!isEmployeeLocked}
                readOnly={isEmployeeLocked}
                isLoading={employeeSearch.isLoading}
                onSearchOptions={(text) => employeeSearch.search(text)}
                searchOptions={employeeSearch.options}
                loadOptionsOnOpen
                onChange={onChange}
                error={errors?.employee_id?.message as string | undefined}
              />
            )}
            control={control}
            name={"employee_id"}
          />
          {editableFields.map((field) => (
            <Stack key={`field-${field.slug}`} gap="xs">
              {renderFieldByType(field)}
            </Stack>
          ))}
        </Stack>
      </form>
    </Modal>
  );
}
